/**
 * autoSubmitRoom.js - a graded room joins the data without being asked.
 *
 * 🔴 THE ASK WAS BUYING NOTHING. Until Sep 2 2026 a graded room sat under a
 * link inviting the reader to go and add it to the dataset somewhere else. They
 * had already handed over the whole league: the paste IS the room, parsed, at
 * full accuracy, ten seats at once. A second explicit step there was not
 * consent, it was friction on work already done, and the report says out loud
 * what happens instead (see the footer line on TeamReportView).
 *
 * 🔴 BOTH PATHS NOW, AND THE HAND-BUILT ONE SENDS *LESS* THAN IT USED TO
 * (Sep 3 2026). This was the paste path only, on the argument that a room typed
 * seat by seat should leave on an explicit tap. That argument does not survive
 * looking at what the tap actually sent: SendRoomCard posted every team under
 * `t.name` - the manager names the user typed in - where this module has always
 * dropped them. So the button was not protecting a stricter boundary, it was
 * guarding a looser payload. Somebody who types ten rosters into a grading page
 * has done more work than somebody who pasted one, not less, and asking them
 * afterwards for a thing they plainly came to do was friction on finished work.
 *
 * What is unchanged: the payload, which is still seat-indexed and nameless, and
 * the disclosure, which now renders on every surface that sends rather than on
 * one of them.
 *
 * 🔴 PRICES OR NOTHING. A room typed without dollars has no price signal to
 * contribute - the corpus is a market, and a roster with no market in it is a
 * list of names - so it sends nothing at all. The caller owns that gate, because
 * the caller is what holds the graded reports; this module posts what it is
 * handed.
 *
 * 🔴 PROVENANCE IS NEVER COLLAPSED. `source` distinguishes "rate_paste" from
 * "rate_manual" and rides in the body, so the two populations stay separable
 * downstream forever. It is therefore part of the send key: the same room
 * arriving by two routes is two events, deliberately, and only a resend of the
 * same room by the same route is suppressed.
 *
 * 🔴 NO NAMES. The payload is seat-indexed: player, position, slot, price, and
 * the seat the roster sat in. The team names the paste read off the transcript
 * are real people's manager names and the corpus has never needed them, so they
 * are dropped here rather than filtered server-side. The league's own name does
 * not go either, exactly as SendRoomCard's does not.
 *
 * 🔴 FIRE AND FORGET, ONE ATTEMPT. A submit that fails must never block, delay,
 * retry or say anything: the reader came for a grade and a corpus write is not
 * their problem. Every path out of here is synchronous and the promise is
 * swallowed.
 *
 * DEDUP IS BOTH ENDS. The server keys on a content hash so a resend of an
 * unchanged room writes nothing; this module keeps its own key set so a resend
 * is not even attempted when a re-render, a step change or a walk between seats
 * puts the same room in front of it again.
 */

import { hashString } from "./hashString.js";
import { leagueFormatLabel } from "./leagueFormat.js";

/** Content keys already sent this page load. Module scope: one room, one POST. */
const sent = new Set();

/**
 * The body a room posts, or null when there is no room to post.
 *
 * Rebuilt field by field, the same rule the endpoint follows on the other side:
 * whatever `settings` and `teams` carry - a league name, a saved-league id, a
 * ladder key - only these fields can ever leave.
 *
 * @param {object} settings - the league in force: teams, budget, positions, scoring
 * @param {Array}  teams    - [{ entries: [{ player, pos, slot, price }] }], seat order
 * @param {string} source   - which flow sent it
 * @param {string} [scoringPreset] - the named lens the grade was computed under
 */
export function roomBody({ settings, teams, source, scoringPreset }) {
  if (!settings || !Array.isArray(teams) || teams.length === 0) return null;
  const rows = teams.map((t, seat) => ({
    seat,
    entries: (t.entries || []).map((e) => ({
      player: e.player,
      pos: e.pos,
      slot: e.slot,
      price: e.price,
    })),
  }));
  if (rows.reduce((a, t) => a + t.entries.length, 0) === 0) return null;
  return {
    settings: {
      teams: settings.teams,
      budget: settings.budget,
      format: leagueFormatLabel(settings.positions),
      qbSlots: Number(settings.positions?.QB) || 0,
      scoringPreset: scoringPreset || null,
      positions: settings.positions,
      scoring: settings.scoring,
    },
    teams: rows,
    source: source || "rate_paste",
  };
}

/**
 * The exact bytes a room is deduped on. One definition, used by the send below
 * and by anything else that needs to ask "is this the same room".
 *
 * 🔴 IT INCLUDES `source`, AND THE SAVED-ROOM ID DELIBERATELY DOES NOT. Two
 * different questions: this one is "has this exact submission already gone",
 * where the route is part of the event; rateSavedRooms asks "is this the same
 * league", where it is not, and calls roomContentId WITHOUT a source so a room
 * keeps one identity however it arrived. Callers comparing against a saved id
 * must omit source the same way.
 */
function keyOf(body) {
  return JSON.stringify(body);
}

/** The content key for a room, or null when there is no room. */
export function roomContentKey(args) {
  const body = roomBody(args);
  return body ? keyOf(body) : null;
}

/**
 * The same identity, short enough to file under.
 *
 * 🔴 IT IS THE CONTENT, NOT THE FLOW. The key is built from the posted body,
 * which carries the league's shape, its scoring and every seat's picks and
 * prices - and no names. So two grades of the same paste are one id, which is
 * exactly what the saved-room store needs: re-grading updates its entry rather
 * than filing a second copy of the same room. It is also the same identity the
 * server dedupes on, so a room already sent and a room already saved cannot
 * disagree about whether they are the same room.
 */
export function roomContentId(args) {
  const key = roomContentKey(args);
  return key ? hashString(key) : null;
}

/**
 * Send a room once, silently.
 *
 * @returns {object|null} the body that was posted, or null when nothing was
 *   (no room, or this exact room already went). Returned for the tests and for
 *   nothing else: no caller renders, awaits or branches on it.
 */
export function autoSubmitRoom(args) {
  const body = roomBody(args);
  if (!body) return null;
  const key = keyOf(body);
  if (sent.has(key)) return null;
  sent.add(key);
  try {
    const p = fetch("/api/rate-room", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: key,
    });
    // A rejected fetch with nobody listening is an unhandled rejection, which
    // is noise in the console and a failed test run in CI. Swallowed, not
    // handled: there is nothing to do about it and nothing to tell anyone.
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* no fetch, no network, no problem: the report is what the reader came for */
  }
  return body;
}

/** Test seam. Clears the sent-key memory between cases. */
export function resetAutoSubmitRoom() {
  sent.clear();
}
